<template>
  <v-card-text>
    <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-3">
      <div class="d-flex align-center ga-2">
        <v-icon color="primary">mdi-account-multiple</v-icon>
        <div class="text-subtitle-1 font-weight-bold">{{ $t("msg12") }}</div>
      </div>
    </div>

    <v-progress-linear v-if="loadingAll" indeterminate class="mb-3" />

    <v-slide-group
      v-if="!loadingAll && allTeams.length"
      v-model="teamForMembers"
      show-arrows
      class="py-2"
      @update:modelValue="onTeamForMembersChanged"
    >
      <v-slide-group-item
        v-for="t in allTeams"
        :key="t.id"
        :value="t.id"
        v-slot="{ isSelected, toggle }"
      >
        <v-chip
          class="ma-1"
          :variant="isSelected ? 'elevated' : 'tonal'"
          :color="isSelected ? 'primary' : 'default'"
          @click="toggle"
          label
        >
          <v-icon start>mdi-account-group</v-icon>
          {{ t.teamname }}
        </v-chip>
      </v-slide-group-item>
    </v-slide-group>

    <v-alert
      v-if="!loadingAll && !allTeams.length"
      type="info"
      variant="tonal"
      class="mb-3"
    >
      {{ $t("msg5") }}<b>{{ $t("Equipos") }}</b
      >.
    </v-alert>

    <v-divider class="my-4" />

    <v-row>
      <!-- Lista de miembros -->
      <v-col cols="12" md="6">
        <v-card rounded="lg" variant="outlined" class="cool-card">
          <v-card-title class="d-flex align-center justify-space-between">
            <div class="d-flex align-center ga-2">
              <v-icon color="primary">mdi-account-multiple</v-icon>
              <span class="text-subtitle-1 font-weight-bold">{{
                $t("msg7")
              }}</span>
            </div>
            <v-chip v-if="teamForMembers" color="primary" variant="tonal">
              Team ID: {{ teamForMembers }}
            </v-chip>
          </v-card-title>
          <v-divider />
          <v-card-text>
            <v-alert
              v-if="!teamForMembers"
              type="info"
              variant="tonal"
              class="mb-3"
            >
              {{ $t("msg6") }}
            </v-alert>

            <!-- EDITOR: Cambiar líder -->
            <v-card
              v-if="teamForMembers"
              variant="tonal"
              rounded="lg"
              class="pa-3 mb-3"
            >
              <div
                class="d-flex align-center justify-space-between flex-wrap ga-2"
              >
                <div class="d-flex align-center ga-2">
                  <v-icon color="primary">mdi-pencil</v-icon>
                  <div class="font-weight-medium">{{ $t("msg8") }}</div>
                </div>

                <div
                  class="d-flex align-center ga-2"
                  style="min-width: 320px; flex: 1; justify-content: flex-end"
                >
                  <v-autocomplete
                    v-model="editBossId"
                    :items="usersLider"
                    item-title="label"
                    item-value="id"
                    :label="$t('lider3')"
                    density="comfortable"
                    hide-details
                    clearable
                    :loading="loadingUsers"
                    :disabled="loadingUsers || usersLider.length === 0"
                    style="max-width: 320px"
                  />
                  <v-btn
                    color="success"
                    variant="elevated"
                    :disabled="!teamForMembers"
                    :loading="savingBoss"
                    @click="saveBoss"
                  >
                    {{ $t("guardar") }}
                  </v-btn>
                </div>
              </div>
            </v-card>

            <!-- DISPLAY: Líder -->
            <v-card
              v-if="teamForMembers"
              rounded="lg"
              variant="outlined"
              class="pa-3 mb-3"
            >
              <div class="d-flex align-center ga-2">
                <v-icon color="primary">mdi-crown</v-icon>
                <div class="font-weight-medium">{{ $t("lider") }}:</div>

                <v-chip v-if="currentBossLabel" color="primary" variant="tonal">
                  {{ currentBossLabel }}
                </v-chip>

                <span v-else class="text-caption text-medium-emphasis">{{
                  $t("lider4")
                }}</span>
              </div>
            </v-card>

            <v-progress-linear
              v-if="loadingMembers"
              indeterminate
              class="mb-3"
            />

            <!--  MIEMBROS (FILTRADOS: nunca mostrar boss aquí) -->
            <v-list v-if="teamForMembers && !loadingMembers">
              <v-list-item v-for="u in teamMembersFiltered" :key="u.id">
                <template #prepend>
                  <v-avatar color="primary" variant="tonal">
                    <v-icon>mdi-account</v-icon>
                  </v-avatar>
                </template>

                <v-list-item-title class="font-weight-medium">
                  {{ u.username || "User " + u.id }}
                </v-list-item-title>
                <v-list-item-subtitle class="text-medium-emphasis">
                  {{ u.email || "" }}
                </v-list-item-subtitle>

                <template #append>
                  <v-tooltip :text="$t('remove_from_team')" location="top">
                    <template #activator="{ props }">
                      <v-btn
                        v-bind="props"
                        icon
                        variant="text"
                        color="error"
                        @click="removeMember(u.id)"
                      >
                        <v-icon>mdi-account-remove</v-icon>
                      </v-btn>
                    </template>
                  </v-tooltip>
                </template>
              </v-list-item>

              <v-list-item v-if="teamMembersFiltered.length === 0">
                <v-list-item-title class="text-medium-emphasis">
                  {{ $t("msg9") }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Agregar miembros -->
      <v-col cols="12" md="6">
        <v-card rounded="lg" variant="outlined" class="cool-card">
          <v-card-title class="d-flex align-center justify-space-between">
            <div class="d-flex align-center ga-2">
              <v-icon color="primary">mdi-account-plus</v-icon>
              <span class="text-subtitle-1 font-weight-bold">{{
                $t("msg10")
              }}</span>
            </div>
          </v-card-title>
          <v-divider />
          <v-card-text>
            <v-alert type="info" variant="tonal" class="mb-3">
              {{ $t("msg11") }} <b>{{ $t("agregar") }}</b
              >.
            </v-alert>

            <!-- USERS FILTRADOS: no mostrar al boss en el selector -->
            <v-autocomplete
              v-model="selectedMembersToAdd"
              :items="usersFilteredForMembers"
              item-title="label"
              item-value="id"
              :label="$t('username')"
              multiple
              chips
              closable-chips
              clearable
              density="comfortable"
              :disabled="!teamForMembers || loadingUsers"
              :loading="loadingUsers"
              :placeholder="$t('search_username_email')"
            />

            <div class="d-flex justify-end">
              <v-btn
                color="success"
                variant="elevated"
                :disabled="!teamForMembers || selectedMembersToAdd.length === 0"
                :loading="savingMembers"
                @click="addMembers"
              >
                <v-icon start>mdi-account-multiple-plus</v-icon>
                {{ $t("agregar") }} ({{ selectedMembersToAdd.length }})
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-card-text>
</template>
<script>
import { swal } from "@/utils/swal";
import { useSettingsStore } from "@/stores/settings";
import "@/css/Global.css";
import {
  getAllTeams,
  getTeamById,
  getTesterUsers,
  getLeaderUsers,
  getTeamTesters,
  addTeamTesters,
  removeTeamTester,
  patchTeam,
} from "@/api/equipos.js";

export default {
  name: "TabMiembrosEquipo",
  props: {
    apiBase: { type: String, required: true },
    toast: { type: Function, required: true },
    prefillTeamId: { type: [String, Number], default: null },
  },

  data() {
    return {
      loadingAll: false,
      allTeams: [],

      loadingUsers: false,
      users: [],
      usersLider: [],

      teamForMembers: null,
      teamMembers: [],
      loadingMembers: false,
      selectedMembersToAdd: [],
      savingMembers: false,

      editBossId: null,
      savingBoss: false,
    };
  },

  computed: {
    isDark() {
      const settings = useSettingsStore();
      return settings.theme === "dark";
    },

    teamForMembersObj() {
      return this.allTeams.find((t) => t.id === this.teamForMembers) || null;
    },

    bossId() {
      const b = this.teamForMembersObj?.boss;
      if (!b) return null;
      if (typeof b === "object") return b.id ?? null;
      return b;
    },

    teamMembersFiltered() {
      const bossId = this.bossId;
      const arr = Array.isArray(this.teamMembers) ? this.teamMembers : [];
      if (!bossId) return arr;
      return arr.filter((u) => u?.id !== bossId);
    },

    usersFilteredForMembers() {
      const bossId = this.bossId;
      const arr = Array.isArray(this.users) ? this.users : [];
      if (!bossId) return arr;
      return arr.filter((u) => u?.id !== bossId);
    },

    currentBossLabel() {
      const b = this.teamForMembersObj?.boss;
      if (b && typeof b === "object") {
        if (b.username && b.email) return `${b.username} · ${b.email}`;
        if (b.username) return b.username;
        if (b.id) {
          const u = this.users.find((x) => x.id === b.id);
          return u?.label || "";
        }
        return "";
      }

      const bossId = b ?? this.editBossId;
      if (!bossId) return "";
      const u = this.users.find((x) => x.id === bossId);
      return u?.label || "";
    },
  },

  watch: {
    prefillTeamId: {
      immediate: true,
      async handler(val) {
        if (!val) return;
        this.teamForMembers = val;
        await this.onTeamForMembersChanged();
        this.$emit("clearPrefill");
      },
    },
  },

  async mounted() {
    await Promise.all([
      this.fetchAllTeams(),
      this.fetchUsers(),
      this.fetchUsersLider(),
    ]);

    if (this.prefillTeamId) {
      this.teamForMembers = this.prefillTeamId;
      await this.onTeamForMembersChanged();
      this.$emit("clearPrefill");
    }
  },

  methods: {
    swalCfg(icon, title, text, timer = 2200) {
      return {
        icon,
        title,
        text,
        timer,
        showConfirmButton: false,
        customClass: {
          popup: this.isDark ? "mi-popup" : "mi-popup-2",
          title: "mi-titulo",
        },
      };
    },

    async fetchUsers() {
      this.loadingUsers = true;
      try {
        const raw = await getTesterUsers();
        this.users = (raw || []).map((u) => ({
          id: u.id,
          label: `${u.username || this.$t("user_fallback", { id: u.id })}${
            u.email ? " · " + u.email : ""
          }`,
        }));
      } catch {
        this.users = [];
       await swal(
          this.swalCfg("error", this.$t("error"), this.$t("users_load_error")),
        );
      } finally {
        this.loadingUsers = false;
      }
    },

    async fetchUsersLider() {
      this.loadingUsers = true;
      try {
        const raw = await getLeaderUsers();
        this.usersLider = (raw || []).map((u) => ({
          id: u.id,
          label: `${u.username || "User " + u.id}${
            u.email ? " · " + u.email : ""
          }`,
        }));
      } catch {
        this.usersLider = [];
        await swal(this.swalCfg("error", "Error", "No pude cargar usuarios"));
      } finally {
        this.loadingUsers = false;
      }
    },

    async fetchAllTeams() {
      this.loadingAll = true;
      try {
        this.allTeams = await getAllTeams();
      } catch {
        this.allTeams = [];
       await swal(this.swalCfg("error", "Error", this.$t("teams_load_error")));
      } finally {
        this.loadingAll = false;
      }
    },

    async fetchTeamDetail(teamId) {
      try {
        const freshTeam = await getTeamById(teamId);
        const idx = this.allTeams.findIndex((t) => t.id === teamId);
        if (idx !== -1) this.allTeams.splice(idx, 1, freshTeam);
        else this.allTeams.push(freshTeam);
        return freshTeam;
      } catch {
        return null;
      }
    },

    async onTeamForMembersChanged() {
      if (!this.teamForMembers) return;

      this.selectedMembersToAdd = [];
      this.teamMembers = [];
      this.editBossId = null;

      const fresh = await this.fetchTeamDetail(this.teamForMembers);
      this.editBossId = fresh?.boss?.id ?? null;

      await this.loadTeamMembers();
      await this.fetchUsers();
    },

    async loadTeamMembers() {
      if (!this.teamForMembers) return;

      this.loadingMembers = true;
      try {
        this.teamMembers = await getTeamTesters(this.teamForMembers);
      } catch {
        this.teamMembers = [];
        await swal(
          this.swalCfg("error", "Error", this.$t("team_members_load_error")),
        );
      } finally {
        this.loadingMembers = false;
      }
    },

    async addMembers() {
      if (!this.teamForMembers || this.selectedMembersToAdd.length === 0)
        return;

      const bossId = this.bossId;
      const cleanedIds = this.selectedMembersToAdd.filter(
        (id) => id !== bossId,
      );

      if (cleanedIds.length === 0) {
       await swal(
          this.swalCfg(
            "warning",
            this.$t("warning"),
            this.$t("cannot_add_leader_member"),
          ),
        );
        this.selectedMembersToAdd = [];
        return;
      }

      this.savingMembers = true;
      try {
        await addTeamTesters(this.teamForMembers, cleanedIds);
       await swal(
          this.swalCfg(
            "success",
            this.$t("success"),
            this.$t("members_added"),
            2000,
          ),
        );
        this.selectedMembersToAdd = [];
        await this.loadTeamMembers();
        await this.fetchUsers();
      } catch (err) {
        const msg = err.response?.data?.detail || this.$t("add_members_error");
        await swal(this.swalCfg("error", this.$t("error"), msg));
      } finally {
        this.savingMembers = false;
      }
    },

    async removeMember(userId) {
      if (!this.teamForMembers) return;

      if (this.bossId && userId === this.bossId) {
       await swal(
          this.swalCfg(
            "warning",
            this.$t("warning"),
            this.$t("leader_remove_warning"),
          ),
        );
        return;
      }

      try {
        await removeTeamTester(this.teamForMembers, userId);
        await swal(
          this.swalCfg(
            "success",
            this.$t("success"),
            this.$t("member_removed"),
            2000,
          ),
        );
        await this.loadTeamMembers();
        await this.fetchUsers();
      } catch {
       await swal(
          this.swalCfg("error", "Error", this.$t("remove_member_error")),
        );
      }
    },

    async saveBoss() {
      if (!this.teamForMembers) return;

      this.savingBoss = true;
      try {
        await patchTeam(this.teamForMembers, {
          boss_id: this.editBossId ?? null,
        });
       await swal(
          this.swalCfg(
            "success",
            this.$t("success"),
            this.$t("leader_updated"),
            2000,
          ),
        );
        await this.fetchAllTeams();
        await this.loadTeamMembers();
      } catch (err) {
        const msg = err.response?.data?.detail || this.$t("save_leader_error");
       await swal(this.swalCfg("error", "Error", msg));
      } finally {
        this.savingBoss = false;
      }
    },
  },
};
</script>
